<template>
    <DialogLayout :class-name="'edit-remark-dialog'" @close="handleClose">
        <template #title>{{ dialogTitle }}</template>
        <template #default>
            <el-form ref="formRef" :model="formData" :rules="rules" :label-position="'left'" :label-width="'100px'"
                class="edit-remark-form">
                <el-form-item :label="fieldLabel" prop="remark">
                    <el-input v-model="formData.remark" type="textarea" :placeholder="t('common.pleaseEnter')" :rows="4" maxlength="200"
                        show-word-limit />
                </el-form-item>
            </el-form>
        </template>
        <template #footer>
            <el-button class="confirm-button-default" @click="handleCancel">
                {{ t('common.cancel') }}
            </el-button>
            <el-button type="primary" :loading="submitting" @click="handleConfirm">
                {{ t('common.save') }}
            </el-button>
        </template>
    </DialogLayout>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, type FormInstance, type FormRules } from 'element-plus'
import DialogLayout from './dialog-layout.vue'
import type { EditRemarkDialogProps } from './edit-remark-dialog-types'
import { updateCandidateRemarkApi } from '@/api/candidate'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const props = defineProps<EditRemarkDialogProps>()

const emit = defineEmits<{
    (e: 'confirm'): void
    (e: 'cancel'): void
    (e: 'close'): void
}>()

const formRef = ref<FormInstance>()
const submitting = ref(false)

// 标题与 label 支持外部传入，不传时用默认文案
const dialogTitle = computed(() => props.title || t('candidate.editRemark'))
const fieldLabel = computed(() => props.label ?? t('candidate.remarkInfo'))

// 表单数据
const formData = reactive<{
    remark: string
}>({
    remark: ''
})

// 表单验证规则
const rules: FormRules = {
    // 备注非必填，不需要验证规则
}

// 初始化表单数据（回显）
onMounted(() => {
    if (props.remark) {
        formData.remark = props.remark
    }
})

// 确认保存
const handleConfirm = async () => {
    if (!formRef.value) return

    try {
        // 先验证表单，如果验证失败会抛出错误，不会继续执行
        await formRef.value.validate()
    } catch (error: unknown) {
        // 表单验证失败，不显示额外提示（Element Plus 会自动显示），直接返回，不提交接口
        if (error && typeof error === 'object' && 'fields' in error) {
            return
        }
        // 其他验证错误也直接返回
        return
    }

    // 验证通过后，才执行 API 调用
    try {
        submitting.value = true

        // 调用 API 更新备注
        await updateCandidateRemarkApi({
            interviewId: props.interviewId,
            remark: formData.remark || ''
        })

        ElMessage.success(t('common.saveSuccess'))
        submitting.value = false
        // emit('confirm') 会自动关闭对话框，Promise 会 resolve
        emit('confirm')
    } catch (error: unknown) {
        submitting.value = false
        
        // API 调用失败
        const errorMessage = error && typeof error === 'object' && 'message' in error 
            ? String(error.message) 
            : t('common.saveFailed')
        console.error('保存失败:', error)
        ElMessage.error(errorMessage)
    }
}

// 取消
const handleCancel = () => {
    emit('cancel')
}

// 关闭
const handleClose = () => {
    emit('close')
}
</script>

<style scoped lang="scss">
.edit-remark-form {

    :deep(.el-form-item__label) {
        font-size: 14px;
        font-weight: 500;
        color: #212429;
        line-height: 35px;
        text-align: right;
        padding-right: 12px;
        margin-bottom: 0px;

        &::before {
            display: none;
        }
    }

    :deep(.el-textarea) {
        .el-textarea__inner {
            font-size: 14px;
            color: #495057;
            line-height: 22px;
            resize: none;

            &::placeholder {
                color: #C9D3D9;
            }
        }
    }

    :deep(.el-input__count) {
        font-size: 12px;
        color: #86909C;
        background: transparent;
    }
}

:deep(.edit-remark-dialog) {
    width: 512px;

   
}
</style>

<style lang="scss">
.edit-remark-dialog {
    width: 512px !important;
    .dialog-footer {
            display: flex;
            justify-content: flex-end;
            gap: 12px;
            padding: 20px 30px;
    
            .el-button {
                min-width: 80px;
            }
        }
}
</style>

